import axios, { AxiosError } from 'axios';
import { useAuthStore } from '@/store/auth';

const baseURL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1';

export const api = axios.create({
  baseURL,
  timeout: 20_000,
});

api.interceptors.request.use((cfg) => {
  const token = useAuthStore.getState().token;
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Prevent infinite reload loops when the backend rejects a freshly-issued token.
const RELOAD_GUARD_KEY = 'chernova:last-401-reload';

api.interceptors.response.use(
  (r) => r,
  (e: AxiosError<{ error?: string }>) => {
    if (e.response?.status === 401) {
      // Don't clobber the auth flow itself — it manages its own errors.
      const url = e.config?.url ?? '';
      const isAuthEndpoint = url.includes('/auth/telegram') || url.includes('/auth/dev');

      useAuthStore.getState().clear();
      try {
        const tgw = (window as any).Telegram?.WebApp;
        const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
        const tooRecent = Date.now() - lastReload < 30_000;
        if (tgw?.initData && !isAuthEndpoint && !tooRecent) {
          sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
          window.location.reload();
        }
      } catch {
        /* ignore */
      }
    }
    return Promise.reject(e);
  },
);

/**
 * Append the auth token as a query string to /uploads/* URLs so <img src> can fetch them.
 * Pass through full http(s) URLs unchanged.
 */
export const withAuthToken = (url: string | null | undefined): string => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (!url.startsWith('/uploads/')) return url;
  const token = useAuthStore.getState().token;
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
};
